import { ComputedRef, computed, reactive } from 'vue'

import { FilterType, ParcelType, PermissionLevel } from '@/constants'
import { APIResponse, ExportResponse, ExtendedStoreResponse, StoreResponse } from '@/common/interfaces'
import { Wall, Board, Sharing, Task, IWall, Subject, User } from '@/common/classes'

import useApiService from '@/composables/api'
const apiService = useApiService()

interface State {
  templates: Wall[]
  myWalls: Wall[]
  allWalls: Wall[]
  sharedWalls: Wall[]
  trashedWalls: Wall[]

  selectedBoard?: Board
  selectedWall?: Wall
}

const _state: State = reactive({
  templates: [],
  myWalls: [],
  allWalls: [],
  sharedWalls: [],
  trashedWalls: [],

  selectedBoard: undefined,
  selectedWall: undefined,
})

interface Actions {
  reset(): void
  selectWallFromDashboard(wall?: Wall): void
  getUserWalls(mode: string): Promise<StoreResponse>
  getTemplateWalls(): Promise<StoreResponse>
  wallExport(wallId: string, fileName: string, filter: FilterType, languageCode: string): Promise<ExtendedStoreResponse>
  getSubjects(): Promise<ExtendedStoreResponse>
  setSharedWalls(userId: string): void
  setSelectedBoard(board: Board): void
  updateTasks(tasks: Task[]): void
  setReadOnlyStatus(roState: boolean): void
  subscribeToWall(wall_id?: string): Promise<StoreResponse>
  togglePermaLinkControl(wall_id?: string): Promise<StoreResponse>
  getWall(value: string): Promise<StoreResponse>
  updateWall(wall: Wall): Promise<StoreResponse>
  createShare(wallId: string, share: Sharing): Promise<StoreResponse>
  changeShare(wallId: string, sharing: Sharing, parcelType: ParcelType): Promise<StoreResponse>
  createPin(wall: Wall): Promise<StoreResponse>
  deletePin(wall: Wall): Promise<StoreResponse>
  deleteShare(wallId: string, shareId: string): Promise<StoreResponse>
  getPermissionLevelForUser(user: User): PermissionLevel | null
  createWall(wall: Wall, externalDuplicate: boolean): Promise<StoreResponse>
  deleteWall(wallId: string): Promise<StoreResponse>
  resetSelectedWall(): void
  localBoardUpdate(wall: Wall): Board
  updateShares(wall: Wall): void
  updateContributionCount(contributors: string[]): void
  updateContributionCount(contributors: string[]): void
  updateWallDetails(wall: Wall): void
}

interface Getters {
  templates: ComputedRef<Wall[]>
  myWalls: ComputedRef<Wall[]>
  allWalls: ComputedRef<Wall[]>
  sharedWalls: ComputedRef<Wall[]>
  trashedWalls: ComputedRef<Wall[]>
  selectedBoard: ComputedRef<Board | undefined>
  selectedWall: ComputedRef<Wall | undefined>
}

interface ServiceInterface {
  state: State
  actions: Actions
  getters: Getters
}

function useWallStore(): ServiceInterface {
  const actions = {
    reset(): void {
      _state.templates = []
      _state.myWalls = []
      _state.allWalls = []
      _state.sharedWalls = []
      _state.trashedWalls = []

      _state.selectedBoard = undefined
      _state.selectedWall = undefined
    },

    async getUserWalls(mode: string): Promise<StoreResponse> {
      this.reset() // reset states before fetching new walls
      const res = (await apiService.request.fetchUserWalls(mode)) as APIResponse<IWall[]>
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'User walls query contains no records or invalid session'
        console.error(res.errors ? res.errors : error)
      } else {
        _state.allWalls = []
        for (const wall of res.data) _state.allWalls.push(new Wall(wall))
        return { success: true }
      }
      return { success: false, error: res.errors }
    },

    async getTemplateWalls(): Promise<StoreResponse> {
      _state.templates = []
      const res = (await apiService.request.fetchTemplates()) as APIResponse<IWall[]>
      if (res.errors && res.errors.length > 0) {
        const error = 'Templates query contains no records or invalid session'
        console.error(res.errors ? res.errors : error)
      } else if (res.data) {
        _state.templates = []
        for (const wall of res.data) _state.templates.push(new Wall(wall))
        return { success: true }
      }
      // ignore if there is no data -> no templates exist!
      return { success: false, error: res.errors }
    },

    async wallExport(wallId: string, fileName: string, filter: FilterType, languageCode: string): Promise<ExtendedStoreResponse> {
      _state.templates = []
      apiService.request.wallExport(wallId, fileName, filter, languageCode)
      return { success: true, body: {} }
    },

    async getSubjects(): Promise<ExtendedStoreResponse> {
      const res = (await apiService.request.fetchSubjects()) as APIResponse<Subject[]>
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Subjects could not be fetched or invalid session'
        console.error(res.errors ? res.errors : error)
      } else {
        const subjects = res.data as Subject[]
        return { success: true, body: subjects }
      }
      return { success: false, error: res.errors }
    },

    setSharedWalls(userId: string): void {
      _state.sharedWalls = _state.allWalls.filter((w: Wall) => w.users.owner.user_id !== userId) // all walls where user is not owner -> must be shared to them
    },

    setSelectedBoard(board: Board) {
      _state.selectedBoard = board
    },

    //Updatetasks tthat preserves the original wall_id from getting lost
    updateTasks(tasks: Task[]) {
      if (_state.selectedWall) {
        const modifiedWall = new Wall(_state.selectedWall)
        modifiedWall._id = _state.selectedWall._id
        modifiedWall.tasks = tasks // Removed reactive wrapper.
        _state.selectedWall = modifiedWall
      }
    },

    setReadOnlyStatus(roState: boolean) {
      if (_state.selectedWall) {
        const modifiedWall = new Wall(_state.selectedWall)
        modifiedWall.status.readOnly = roState
        _state.selectedWall = modifiedWall
      }
    },

    selectWallFromDashboard(wall?: Wall) {
      _state.selectedWall = wall
    },

    async subscribeToWall(wall_id?: string): Promise<StoreResponse> {
      const id = wall_id || _state.selectedWall?._id || ''
      if (!id) return { success: false, error: new Error('No wall to subscribe to!') }
      const res = (await apiService.request.subscribeToWall(id)) as APIResponse<IWall>
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Error subscribing to Wall'
        console.error(res.errors ? res.errors : error)
      } else {
        _state.selectedWall = new Wall(res.data)
        return { success: true }
      }
      return { success: false, error: res.errors }
    },

    async togglePermaLinkControl(wall_id?: string): Promise<StoreResponse> {
      const res = (await apiService.request.togglePermaLinkControl(wall_id ?? '')) as APIResponse<IWall>
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Error joining wall by permaLink'
        console.error(res.errors ? res.errors : error)
      } else {
        _state.selectedWall = new Wall(res.data)
        return { success: true }
      }
      return { success: false, error: res.errors }
    },

    async getWall(value: string): Promise<StoreResponse> {
      const res = (await apiService.request.getWall(value)) as APIResponse<IWall>
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Error getting Wall'
        console.error(res.errors ? res.errors : error)
      } else {
        _state.selectedWall = new Wall(res.data)
        return { success: true }
      }
      return { success: false, error: res.errors }
    },

    async updateWall(wall: Wall): Promise<StoreResponse> {
      const res = (await apiService.request.updateWall(wall._id, wall)) as APIResponse<IWall>
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Wall could not be updated or invalid session'
        console.error(res.errors ? res.errors : error)
      } else {
        _state.selectedWall = new Wall(res.data)
        return { success: true }
      }
      return { success: false, error: res.errors }
    },

    async createShare(wallId: string, share: Sharing): Promise<StoreResponse> {
      const res = (await apiService.request.createShare(wallId, share)) as APIResponse<IWall>
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Share could not be created or invalid session'
        console.error(res.errors ? res.errors : error)
      } else {
        _state.selectedWall = new Wall(res.data)
        return { success: true }
      }
      return { success: false, error: res.errors }
    },

    async changeShare(wallId: string, sharing: Sharing, parcelType: ParcelType): Promise<StoreResponse> {
      const res = (await apiService.request.changeShare(wallId, sharing._id, sharing, parcelType)) as APIResponse<IWall>
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Share could not be updated or invalid session'
        console.error(res.errors ? res.errors : error)
      } else {
        _state.selectedWall = new Wall(res.data)
        return { success: true }
      }
      return { success: false, error: res.errors }
    },

    async createPin(wall: Wall): Promise<StoreResponse> {
      const res = (await apiService.request.createPin(wall)) as APIResponse<IWall>
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Pin could not be created or invalid session'
        console.error(res.errors ? res.errors : error)
      } else {
        _state.selectedWall = new Wall(res.data)
        return { success: true }
      }
      return { success: false, error: res.errors }
    },

    async deletePin(wall: Wall): Promise<StoreResponse> {
      const res = (await apiService.request.deletePin(wall)) as APIResponse<IWall>
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Pin could not be deleted or invalid session'
        console.error(res.errors ? res.errors : error)
      } else {
        _state.selectedWall = new Wall(res.data)
        return { success: true }
      }
      return { success: false, error: res.errors }
    },

    async deleteShare(wallId: string, shareId: string): Promise<StoreResponse> {
      const res = (await apiService.request.deleteShare(wallId, shareId)) as APIResponse<IWall>
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Share could not be removed or invalid session'
        console.error(res.errors ? res.errors : error)
      } else {
        _state.selectedWall = new Wall(res.data)
        return { success: true }
      }
      return { success: false, error: res.errors }
    },

    getPermissionLevelForUser(user: User): PermissionLevel | null {
      let currentPermissionIndex = -1
      const permissionOrder = [PermissionLevel.Copy, PermissionLevel.Join, PermissionLevel.Organize, PermissionLevel.Owner]
      const shares = _state.selectedWall?.users.shares.filter((s) => user.profile.groups.some((g) => g.originalId === s.group_id))
      if (shares) {
        for (const share of shares) {
          const newIndex = permissionOrder.indexOf(share.permissionLevel)
          if (newIndex > currentPermissionIndex) currentPermissionIndex = newIndex
        }
      }
      return currentPermissionIndex !== -1 ? permissionOrder[currentPermissionIndex] : null
    },

    async createWall(wall: Wall, externalDuplicate = false): Promise<StoreResponse> {
      const res = await apiService.request.createWall(wall)
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Wall could not be created or invalid session'
        console.error(res.errors ? res.errors : error)
      } else {
        const newWall = new Wall(res.data)
        _state.allWalls.push(newWall)
        if (!externalDuplicate) _state.selectedWall = newWall // default select after creation
        return { success: true, newWallId: newWall._id }
      }
      return { success: false, error: res.errors }
    },

    async deleteWall(wallId: string): Promise<StoreResponse> {
      const res = await apiService.request.deleteWall(wallId)
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'Wall could not be deleted or invalid session'
        console.error(res.errors ? res.errors : error)
      } else {
        _state.allWalls.splice(
          _state.allWalls.findIndex((w) => w._id === wallId),
          1,
        )
        return { success: true }
      }
      return { success: false, error: res.errors }
    },

    resetSelectedWall() {
      _state.selectedWall = undefined
    },

    /* LOCAL UPDATES (INVOKED BY INCOMING PARCELS) */

    localBoardUpdate(wall: Wall): Board {
      if (_state.selectedWall) {
        const previousWallSpec = new Wall(_state.selectedWall)
        const modifiedWall = new Wall(previousWallSpec)
        modifiedWall.boards = wall.boards
        _state.selectedWall = modifiedWall
        for (const board of wall.boards) {
          if (previousWallSpec.boards.findIndex((b) => b._id === board._id) === -1) return board // return new board
        }
      }
      return wall.boards[wall.boards.length - 1] // return last board known
    },

    updateShares(wall: Wall) {
      if (_state.selectedWall && wall._id === _state.selectedWall._id) {
        const modifiedWall = new Wall(_state.selectedWall)
        modifiedWall.users.shares = wall.users.shares
        _state.selectedWall = modifiedWall
      }
    },

    updateContributionCount(contributors: string[]) {
      if (_state.selectedWall) {
        const modifiedWall = new Wall(_state.selectedWall)
        modifiedWall.details.contributors = contributors
        _state.selectedWall = modifiedWall
      }
    },

    updateWallDetails(wall: Wall) {
      if (_state.selectedWall) {
        const modifiedWall = new Wall(_state.selectedWall)
        modifiedWall.details.title = wall.details.title
        modifiedWall.details.description = wall.details.description
        _state.selectedWall = modifiedWall
      }
    },
  }

  const getters = {
    get templates(): ComputedRef<Wall[]> {
      return computed(() => _state.templates)
    },

    get myWalls(): ComputedRef<Wall[]> {
      return computed(() => _state.myWalls)
    },

    get allWalls(): ComputedRef<Wall[]> {
      return computed(() => _state.allWalls)
    },

    get sharedWalls(): ComputedRef<Wall[]> {
      return computed(() => _state.sharedWalls)
    },

    get trashedWalls(): ComputedRef<Wall[]> {
      return computed(() => _state.trashedWalls)
    },

    get selectedBoard(): ComputedRef<Board | undefined> {
      return computed(() => _state.selectedBoard)
    },

    get selectedWall(): ComputedRef<Wall | undefined> {
      return computed(() => _state.selectedWall)
    },
  }
  const state = _state
  return { state, actions, getters }
}

export default useWallStore
